import React, { useEffect, useRef, useState } from "react"
import {
  Backdrop,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  styled
} from "@mui/material"
import AppPagination from "components/StyledComponents/AppPagination"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import TableNoRecords from "components/TableNoRecords"
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward"
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward"
import InfoIcon from "@mui/icons-material/Info"
import { useTheme } from "@mui/system"
import { useDispatch } from "react-redux"
import { appActions } from "redux/app/slice/app"
import DeleteConfirmationDialog from "components/DeleteConfirmationDialog"
import { superAdminAction } from "redux/superAdmin/slice/user"
import { deleteAdvertisementsAction, getAdvertisementDetailsAction } from "redux/superAdmin/actions"

const AdvertisementList = ({
  columns = [],
  rows = [],
  setPage,
  currentPage,
  totalPage,
  handleEditAdvertisement
}) => {
  const themes = useTheme()
  const [order, setOrder] = useState(orderStat.DESC)
  const [anchorEl, setAnchorEl] = useState(false)
  const openMenu = Boolean(anchorEl)
  const dispatch = useDispatch()
  const rowId = useRef(null)
  const [deleteAdvertisement, setDeleteAdvertisement] = useState(false)

  const onOptionsClick = (e, id) => {
    e.stopPropagation()
    rowId.current = id
    setAnchorEl(e.currentTarget)
    dispatch(superAdminAction.setAdvertisementID(id))
    dispatch(getAdvertisementDetailsAction({ id }))
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  function handleSort() {
    if (order === orderStat.DESC) {
      setOrder("scheduled_date")
      dispatch(appActions.setOrderBy("scheduled_date"))
    } else {
      setOrder("-scheduled_date")
      dispatch(appActions.setOrderBy("-scheduled_date"))
    }
  }

  useEffect(() => {
    setOrder("-scheduled_date")
    dispatch(appActions.setOrderBy("-scheduled_date"))
  }, [])

  function handleDelete() {
    setDeleteAdvertisement(true)
  }

  function handleDeleteAdvertisement() {
    const id = rowId.current
    dispatch(deleteAdvertisementsAction({ id }))
    rowId.current = null
  }

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    })

  return (
    <>
      <DeleteConfirmationDialog
        title={`Are you sure you want to delete this advertisement?`}
        description="  "
        style={{ textAlign: "center", width: "80%" }}
        onClose={() => setDeleteAdvertisement(false)}
        onDelete={() => handleDeleteAdvertisement()}
        open={deleteAdvertisement}
      />
      <Paper>
        <TableContainer sx={{ maxHeight: 700 }}>
          <CustomTable stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}>
                    <Typography variant="body2Regular" style={{ opacity: 0.6 }}>
                      {column.label}{" "}
                      {column?.tooltip && (
                        <Tooltip title={column.tooltip}>
                          <InfoIcon fontSize="small" />
                        </Tooltip>
                      )}
                      {column?.isSorting && (
                        <span onClick={handleSort} style={{ cursor: "pointer" }}>
                          {order === "-scheduled_date" ? (
                            <ArrowDownwardIcon fontSize="small"></ArrowDownwardIcon>
                          ) : (
                            <ArrowUpwardIcon fontSize="small"></ArrowUpwardIcon>
                          )}
                        </span>
                      )}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length ? (
                rows?.map((row) => {
                  return (
                    <TableRow key={row.id}>
                      <TableCell>
                        <div className="d-flex flex-column">
                          <Typography variant="body1Regular">
                            {row?.banner_heading ?? "-"}
                          </Typography>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1Regular">{row?.tile_heading ?? "-"}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body1Regular">
                          {formatDate(row?.ad_start_date) + " to " + formatDate(row?.ad_end_date) ||
                            "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body1Regular">
                          {formatDate(row?.start_date) + " to " + formatDate(row?.end_date) || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <span onClick={(e) => onOptionsClick(e, row?.id)}>
                          <MoreHorizIcon
                            sx={() => ({
                              height: "24px",
                              width: "24px"
                            })}
                          />
                        </span>
                      </TableCell>
                    </TableRow>
                  )
                })
              ) : (
                <TableNoRecords colSpan={8} label="No New Advertisement" />
              )}
            </TableBody>
          </CustomTable>
        </TableContainer>
        <Backdrop sx={{ color: "#fff", zIndex: 1300 }} open={openMenu} onClick={handleMenuClose}>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right"
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right"
            }}
            PaperProps={{
              sx: {
                marginTop: "15px",
                backgroundColor: themes.palette.background.default,
                borderRadius: "12px",
                "& .MuiList-root": {
                  backgroundColor: themes.palette.background.default,
                  width: "225px",
                  boxSizing: "border-box",
                  padding: "10px 0"
                }
              }
            }}>
            <StyledMenuItem onClick={() => handleEditAdvertisement()}>
              <Typography variant="body1Regular">Edit</Typography>
            </StyledMenuItem>
            <StyledMenuItem onClick={handleDelete}>
              <Typography sx={{ color: themes.palette.red.main }} variant="body1Regular">
                Delete
              </Typography>
            </StyledMenuItem>
          </Menu>
        </Backdrop>
      </Paper>
      {rows?.length ? (
        <AppPagination onPageChange={setPage} totalPages={totalPage} currentPage={currentPage} />
      ) : (
        ""
      )}
    </>
  )
}

export default AdvertisementList

const orderStat = {
  ASC: "scheduled_date",
  DESC: "-scheduled_date"
}

const CustomTable = styled(Table)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  borderCollapse: "collapse !important",
  "& .MuiTableCell-root": {
    padding: "12px 24px 12px 0px",
    backgroundColor: theme.palette.background.main,
    borderBottom: "none",
    boxSizing: "border-box",
    "&:last-child": {
      paddingRight: 0
    }
  },
  "& .MuiTableRow-root": {
    borderBottom: `2px solid #272a2c`,
    "&:last-child": {
      borderBottom: "none"
    }
  },
  "& .MuiTableCell-head": {
    verticalAlign: "bottom",
    position: "sticky",
    borderBottom: `2px solid #272a2c`,
    "&:last-child": {
      paddingRight: 0
    }
  }
}))

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: "12px 24px"
}))
